import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * v2.71.78 — Reliable scroll restoration with anchor-based fallback.
 * (was v2.71.17 Y-based polling, which broke on lazy-image layout shifts.)
 *
 * Behaviour
 * ─────────
 *   • PUSH / REPLACE (forward nav)
 *       → scroll to top (snappy SPA feel)
 *
 *   • POP (browser/swipe back) — TWO-STAGE restore:
 *       Stage A — Anchor-based (preferred):
 *         The clicked card persists its id into sessionStorage under
 *         `he-last-card:{pathname}{search}` before navigating. On POP back
 *         we poll the DOM for `[data-testid="property-card-{id}"]`, and
 *         when it appears we `scrollIntoView` it. This is layout-shift
 *         proof — the user always lands on the exact card they tapped.
 *       Stage B — Y-based (fallback):
 *         If no card id is saved (e.g. user navigated via a different
 *         link, or sessionStorage is disabled), fall back to the Y
 *         coordinate restorer.
 *
 * Why the scroll-event cache exists (Stage B)
 * ───────────────────────────────────────────
 *   When the user clicks a property card, React Router commits the new
 *   route synchronously. By the time our `useEffect` deps-change handler
 *   fires, the browser may have reset `window.scrollY` to 0 (because the
 *   freshly-mounted detail page is shorter than the listing). So reading
 *   `window.scrollY` at that moment would always read 0 → save-time-bug.
 *   We maintain a live `scrollCacheRef` updated on every scroll event,
 *   and save its value (the listing's scrollY at the LAST scroll, just
 *   before the click).
 *
 * Storage
 *   • sessionStorage keyed by:
 *       `he-scroll:{pathname}{search}`        → Y coordinate (Stage B)
 *       `he-last-card:{pathname}{search}`     → card id    (Stage A)
 *   • Survives soft refresh; cleared on tab close
 *   • Also saved on `pagehide` so hard reload + back still restores
 *
 * Edge cases
 *   • Hash links (`/#section`)               — untouched, browser-native
 *   • Destination shorter than saved Y       — clamped to maxScrollable
 *   • Anchor poll timeout (3s)               — falls back to Y coord
 *   • Lazy-loaded images shift docHeight     — handled by anchor stage;
 *                                              Y stage's poll budget
 *                                              extended to 3s for safety
 */
const STORAGE_PREFIX = "he-scroll:";
const LAST_CARD_PREFIX = "he-last-card:";
const POLL_INTERVAL_MS = 50;
// v2.71.78 — Y-based fallback budget raised from 1.5s to 3s to better
// survive lazy-image hydration on slow networks.
const POLL_BUDGET_MS = 3000;
// v2.71.78 — Anchor-based polling: we wait up to ANCHOR_POLL_BUDGET_MS for
// the saved card to appear in the DOM (data hydration), then we keep
// re-applying scrollIntoView for ANCHOR_STABILISE_MS to defeat layout
// shifts from lazy images loading above the card.
// v2.75 — Phase 2: budget extended from 3s → 5s. The new restoration path
// (Dashboard's useProperties initialPage=N) issues ONE larger page_size
// request to reconstruct prior browsing depth — on a cold network this
// can take 1.5-3s. The extra 2s buffer ensures the anchor card ALWAYS
// makes it into the DOM before we fall back to Y-coordinate restoration.
const ANCHOR_POLL_BUDGET_MS = 5000;
const ANCHOR_STABILISE_MS = 800;

function scrollKey(pathname, search) {
  return STORAGE_PREFIX + pathname + (search || "");
}
function cardKey(pathname, search) {
  return LAST_CARD_PREFIX + pathname + (search || "");
}

function saveScroll(pathname, search, y) {
  try {
    sessionStorage.setItem(scrollKey(pathname, search), String(y || 0));
  } catch (_) { /* sessionStorage blocked (private-mode Safari) */ }
}

function readScroll(pathname, search) {
  try {
    const v = sessionStorage.getItem(scrollKey(pathname, search));
    return v == null ? null : parseInt(v, 10);
  } catch (_) { return null; }
}

function readLastCard(pathname, search) {
  try {
    return sessionStorage.getItem(cardKey(pathname, search)) || null;
  } catch (_) { return null; }
}

function clearLastCard(pathname, search) {
  try {
    sessionStorage.removeItem(cardKey(pathname, search));
  } catch (_) { /* noop */ }
}

/**
 * Try to find + scroll to the saved card. Returns true if we managed to
 * locate the element within ANCHOR_POLL_BUDGET_MS, false otherwise.
 * Keeps re-applying scrollIntoView for ANCHOR_STABILISE_MS after we find
 * it, to counter layout shifts from lazy images loading.
 */
function restoreToCard(cardId, onComplete, onFail) {
  if (!cardId) { onFail?.(); return; }
  const selector = `[data-testid="property-card-${cardId}"]`;
  let elapsed = 0;
  let foundEl = null;

  const tick = () => {
    if (!foundEl) {
      foundEl = document.querySelector(selector);
      if (!foundEl) {
        elapsed += POLL_INTERVAL_MS;
        if (elapsed >= ANCHOR_POLL_BUDGET_MS) {
          onFail?.();
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      // Found — switch to stabilisation phase.
      elapsed = 0;
    }
    // Re-apply scroll on each tick to defeat layout-shift drift.
    try {
      foundEl.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "instant",
      });
    } catch (_) {
      // Older Safari doesn't support behavior:"instant" — use the
      // 2-arg form which is "always instant".
      foundEl.scrollIntoView(true);
    }
    elapsed += POLL_INTERVAL_MS;
    if (elapsed >= ANCHOR_STABILISE_MS) {
      onComplete?.();
      return;
    }
    setTimeout(tick, POLL_INTERVAL_MS);
  };
  requestAnimationFrame(tick);
}

/**
 * Y-based fallback restorer. Re-applies scrollTo on every poll tick until
 * docHeight stabilises for STABLE_FRAMES consecutive ticks or POLL_BUDGET_MS
 * exhausts.
 */
function restoreToY(targetY) {
  const STABLE_FRAMES = 3;
  let lastDocH = -1;
  let stableCount = 0;
  let elapsed = 0;
  const tick = () => {
    const docH = document.documentElement.scrollHeight;
    const viewH = window.innerHeight;
    const maxScrollable = Math.max(0, docH - viewH);
    window.scrollTo({
      top: Math.min(targetY, maxScrollable),
      left: 0,
      behavior: "instant",
    });
    if (docH === lastDocH) {
      stableCount += 1;
    } else {
      stableCount = 0;
      lastDocH = docH;
    }
    if (stableCount >= STABLE_FRAMES || elapsed >= POLL_BUDGET_MS) return;
    elapsed += POLL_INTERVAL_MS;
    setTimeout(tick, POLL_INTERVAL_MS);
  };
  requestAnimationFrame(tick);
}

export default function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const prevLocation = useRef({ pathname, search });
  const scrollCacheRef = useRef(0);

  // One-time setup: scroll listeners, scrollRestoration override, persistence.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const onScroll = () => {
      scrollCacheRef.current = window.scrollY || 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Pin cache at pointerdown so any pre-click auto-scroll (browser's own
    // scrollIntoView when tapping a partially-visible link) doesn't corrupt
    // the saved position. pointerdown fires synchronously BEFORE the browser
    // adjusts scroll for the tapped element.
    const onPointerDown = () => {
      scrollCacheRef.current = window.scrollY || 0;
    };
    window.addEventListener("pointerdown", onPointerDown, { passive: true, capture: true });

    // Persist current scrollY on hard reload / tab close so "reload then back"
    // still restores correctly.
    const persist = () => {
      saveScroll(
        window.location.pathname,
        window.location.search,
        scrollCacheRef.current || window.scrollY || 0
      );
    };
    window.addEventListener("beforeunload", persist);
    window.addEventListener("pagehide", persist);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointerdown", onPointerDown, { capture: true });
      window.removeEventListener("beforeunload", persist);
      window.removeEventListener("pagehide", persist);
    };
  }, []);

  useEffect(() => {
    // Hash anchors: leave browser-native behaviour intact.
    if (hash) {
      prevLocation.current = { pathname, search };
      return;
    }

    const prev = prevLocation.current;
    const changed = prev.pathname !== pathname || prev.search !== search;

    // 1) Save OUTGOING route's last-observed scrollY (from cache, not live —
    //    by now window.scrollY may already be 0 if the new page mounted shorter).
    if (changed) {
      saveScroll(prev.pathname, prev.search, scrollCacheRef.current);
    }

    // 2) At the NEW location: restore on POP, top on PUSH/REPLACE.
    if (navType === "POP") {
      // Stage A — anchor-based restore (preferred)
      const cardId = readLastCard(pathname, search);
      if (cardId) {
        restoreToCard(
          cardId,
          () => {
            // Success — clear the breadcrumb so a second visit to the same
            // listing URL doesn't re-pull to the old card.
            clearLastCard(pathname, search);
          },
          () => {
            // Anchor not found within budget — fall back to Y-based restore.
            const target = readScroll(pathname, search);
            if (target == null || target <= 0) {
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            } else {
              restoreToY(target);
            }
          }
        );
      } else {
        // Stage B — Y-based restore (no card breadcrumb)
        const target = readScroll(pathname, search);
        if (target == null || target <= 0) {
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        } else {
          restoreToY(target);
        }
      }
    } else {
      // PUSH / REPLACE — fresh navigation. Default behaviour is to land
      // at top, but skip the reset when ONLY the query string changed
      // within the same pathname (filter pill clicks, sort changes,
      // pagination). This mirrors React Router v6.4's
      // `preventScrollReset: true` contract and is the canonical fix
      // for "filter chip jumps the page to top".
      //
      // v2.87.x — Root cause of the persistent "intent chip jumps page"
      // bug on HouseEntry.in. Every filter URL sync calls
      // nav(target, {replace:true}) which used to fall through here and
      // trigger window.scrollTo(0). Now we skip the reset for query-only
      // REPLACE/PUSH so the chip bar stays exactly where the user left it.
      const onlyQueryChanged = prev.pathname === pathname && prev.search !== search;
      if (!onlyQueryChanged) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    }

    prevLocation.current = { pathname, search };
  }, [pathname, search, hash, navType]);

  return null;
}
